import {
    Box,
    Card,
    CardBody,
    Center,
    Grid,
    GridItem,
    Spinner,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Text,
} from '@chakra-ui/react';
import React, { type ReactNode } from 'react';

import { usePortfolio } from '../api/portfolio';
import { PortfolioAllocationTableTab } from './Tabs/PortfolioAllocationTableTab';
import { PortfolioClosedLotsTableTab } from './Tabs/PortfolioClosedLotsTableTab';
import { PortfolioDetailsTab } from './Tabs/PortfolioDetailsTab';
import { createLinesPortfolioIdentification } from './Tabs/PortfolioDetailsTab';
import { PortfolioOpenLotsTableTab } from './Tabs/PortfolioOpenLotsTableTab';
import { PortfolioPositionsTableTab } from './Tabs/PortfolioPositionsTableTab';
import { PortfolioRestrictionsTableTab } from './Tabs/PortfolioRestrictionsTableTab';
import { PortfolioTradeHistoryTableTab } from './Tabs/PortfolioTradeHistoryTableTab';
import { PortfolioTransactionsTableTab } from './Tabs/PortfolioTransactionsTableTab';
import { PortfolioWithdrawalsTableTab } from './Tabs/PortfolioWithdrawalsTableTab';

export type DataGridLine = {
    label: string;
    value: ReactNode;
};

const PortfolioIdentificationGrid = ({ data }: { data: DataGridLine[] }) => {
    return (
        <Box>
            <Grid
                templateColumns="min-content 1fr min-content 1fr min-content 1fr"
                gap={0.2}
                borderWidth="2px"
                borderColor="whiteAlpha.40"
                p={2}
            >
                {data.map(({ label, value }, index) => (
                    <React.Fragment key={`${label}-${index}`}>
                        <GridItem display="flex" alignItems="center">
                            <Text
                                fontSize="xs"
                                fontWeight="bold"
                                color="gray.400"
                                textTransform="uppercase"
                                letterSpacing="wider"
                                mr={4}
                                whiteSpace="nowrap"
                            >
                                {label}
                            </Text>
                        </GridItem>
                        <GridItem display="flex" alignItems="center">
                            <Text fontSize="sm" fontWeight="medium" color="white">
                                {value}
                            </Text>
                        </GridItem>
                        {index === 2 && (
                            <GridItem colSpan={6} borderBottomWidth="1px" borderColor="whiteAlpha.30" my={1} />
                        )}
                    </React.Fragment>
                ))}
            </Grid>
        </Box>
    );
};

export const PortfolioTabs = ({ pId, defaultIndex }: { pId: string; defaultIndex?: number }) => {
    const { data, isPending } = usePortfolio({ pId });

    if (isPending) {
        return (
            <Center width="100%" height="40vh">
                <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="teal.500" size="lg" />
            </Center>
        );
    }

    return (
        <Box>
            {data && (
                <Grid templateColumns="1fr" gap={1} width="100%" alignItems="start" mb={4}>
                    <Card>
                        <CardBody>
                            <PortfolioIdentificationGrid data={createLinesPortfolioIdentification(data)} />
                        </CardBody>
                    </Card>
                </Grid>
            )}
            <Tabs colorScheme="teal" defaultIndex={defaultIndex} isFitted isLazy>
                <TabList>
                    <Tab>Allocation</Tab>
                    <Tab>Details</Tab>
                    <Tab>Positions</Tab>
                    <Tab>Withdrawals</Tab>
                    <Tab>Restrictions</Tab>
                    <Tab>Open Lots</Tab>
                    <Tab>Closed Lots</Tab>
                    <Tab>Trade History</Tab>
                    <Tab>Transactions</Tab>
                </TabList>

                <TabPanels>
                    <TabPanel>
                        <PortfolioAllocationTableTab pId={pId} />
                    </TabPanel>
                    <TabPanel>
                        <PortfolioDetailsTab pId={pId} />
                    </TabPanel>
                    <TabPanel>
                        <PortfolioPositionsTableTab pId={pId} />
                    </TabPanel>
                    <TabPanel>
                        <PortfolioWithdrawalsTableTab pId={pId} />
                    </TabPanel>
                    <TabPanel>
                        <PortfolioRestrictionsTableTab key={pId} pId={pId} />
                    </TabPanel>
                    <TabPanel>
                        <PortfolioOpenLotsTableTab pId={pId} />
                    </TabPanel>
                    <TabPanel>
                        <PortfolioClosedLotsTableTab pId={pId} />
                    </TabPanel>
                    <TabPanel>
                        <PortfolioTradeHistoryTableTab pId={pId} />
                    </TabPanel>
                    <TabPanel>
                        <PortfolioTransactionsTableTab pId={pId} />
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </Box>
    );
};
